import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface AuditLogEntry {
  id: string;
  user_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: Record<string, never> | null;
  created_at: string;
}

export interface AuditLogListPayload {
  logs: AuditLogEntry[];
  total: number;
}

// ── Server Functions ──────────────────────────────────────
export const getAuditLogs = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      page: z.number().int().min(1).default(1),
      limit: z.number().int().min(1).max(100).default(20),
      action: z.string().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const offset = (data.page - 1) * data.limit;

    let query = client
      .from("audit_logs")
      .select("id, user_id, action, entity_type, entity_id, details, created_at", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + data.limit - 1);

    if (data.action) {
      query = query.eq("action", data.action);
    }

    const { data: logs, error, count } = await query;

    if (error) return { data: null, error: error.message };

    const entries: AuditLogEntry[] = (logs ?? []).map((log) => ({
      id: String(log.id),
      user_id: String(log.user_id),
      action: String(log.action),
      entity_type: log.entity_type ? String(log.entity_type) : null,
      entity_id: log.entity_id ? String(log.entity_id) : null,
      details: (log.details as Record<string, never>) ?? null,
      created_at: String(log.created_at),
    }));

    return { data: { logs: entries, total: count ?? 0 }, error: null };
  });
